// InstallAppBanner.tsx - Invita al cliente a instalar la app en su celular.
// Android: botón "Instalar" que abre la ventana del sistema. iPhone: guía de 2 pasos.
// No aparece si ya está instalada, en computadora, o si la cerró hace poco.
import React, { useState } from 'react';
import { Download, Share, PlusSquare, X } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useInstallPrompt } from '../shared/utils/installPrompt';
import { useAuth } from '../shared/hooks/useAuth';
import { tenantIconUrl } from '../utils/pwa';

/** Para qué le sirve la app a cada rol */
const SUBTITLE: Record<string, string> = {
    client: 'Rastrea tus paquetes con un toque',
    courier: 'Tus entregas y el escáner a un toque',
    agent: 'Tus clientes y cotizaciones a un toque',
};
const STAFF_SUBTITLE = 'Gestiona tu operación desde el celular';

const DISMISS_KEY = 'install_banner_dismissed_at';
const DISMISS_DAYS = 7;

const dismissedRecently = () => {
    try {
        const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
        return t > 0 && Date.now() - t < DISMISS_DAYS * 864e5;
    } catch { return false; }
};

interface Props {
    /** 'card' dentro del portal; 'light' sobre el fondo de color del inicio de sesión */
    variant?: 'card' | 'light';
    /** Clases extra (p. ej. margen) solo cuando el aviso se muestra */
    className?: string;
}

const InstallAppBanner: React.FC<Props> = ({ variant = 'card', className = '' }) => {
    const { tenant } = useTenant();
    const { userData } = useAuth();
    const subtitle = userData ? (SUBTITLE[userData.role] ?? STAFF_SUBTITLE) : SUBTITLE.client;
    const { canPrompt, ios, installed, mobile, prompt } = useInstallPrompt();
    // ?instalar=1 viene de los botones de la página web (paqueteria.itobox.com):
    // se muestra aunque la persona lo haya cerrado antes, y en iPhone se abre la guía de una vez
    const fromWeb = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('instalar');
    const [hidden, setHidden] = useState(() => !fromWeb && dismissedRecently());
    const [showGuide, setShowGuide] = useState(() => fromWeb && ios && !installed);
    const brandRaw = tenant?.branding?.primary_color;
    const brand = brandRaw && /^#[0-9a-f]{6}$/i.test(brandRaw) ? brandRaw : '#E50914';
    const name = tenant?.branding?.company_name || 'la app';

    // Android sin la ventana del sistema (Chrome aún no la ofrece): guía del menú ⋮
    const androidGuide = !canPrompt && !ios;
    if (installed || hidden || !mobile || (androidGuide && !fromWeb)) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* sin almacenamiento */ }
        setHidden(true);
    };

    const install = async () => {
        if (canPrompt) {
            const ok = await prompt();
            if (ok) setHidden(true);
        } else {
            setShowGuide(true);
        }
    };

    const light = variant === 'light';
    return (
        <>
            <div className={`${className} flex items-center gap-3 rounded-2xl p-3 ${light ? 'bg-white/15 text-white border border-white/25' : 'bg-white border border-[#EEF0F3] shadow-[0_4px_16px_rgba(0,0,0,0.06)]'}`}>
                <img src={tenant?.subdomain === 'itobox' ? '/icons/itobox/icon-192.png' : tenantIconUrl(192)} alt=""
                    className="w-11 h-11 rounded-xl bg-white object-contain flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <b className={`text-sm block ${light ? 'text-white' : 'text-[#111111]'}`}>Instala la app de {name}</b>
                    <span className={`text-xs ${light ? 'text-white/80' : 'text-[#6B7280]'}`}>{subtitle}</span>
                </div>
                <button onClick={install}
                    className="h-9 px-3.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 flex-shrink-0"
                    style={light ? { background: '#FFFFFF', color: brand } : { background: brand, color: '#FFFFFF' }}>
                    <Download size={14} /> Instalar
                </button>
                <button onClick={dismiss} aria-label="Cerrar" className={`p-1 flex-shrink-0 ${light ? 'text-white/70' : 'text-[#9CA3AF]'}`}>
                    <X size={16} />
                </button>
            </div>

            {showGuide && (
                <div className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowGuide(false)}>
                    <div className="bg-white rounded-3xl w-full max-w-sm p-6 text-[#111111]" onClick={e => e.stopPropagation()}
                        style={{ fontFamily: 'Poppins, system-ui, sans-serif', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
                        <div className="flex items-center justify-between mb-4">
                            <b className="text-lg">{ios ? 'Instalar en tu iPhone' : 'Instalar en tu Android'}</b>
                            <button onClick={() => setShowGuide(false)} aria-label="Cerrar" className="p-1 text-[#9CA3AF]"><X size={20} /></button>
                        </div>
                        <ol className="flex flex-col gap-4 text-sm list-none p-0 m-0">
                            <li className="flex items-center gap-3">
                                <span className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ background: brand }}>1</span>
                                {ios
                                    ? <span>Toca el botón <b>Compartir</b> <Share size={16} className="inline -mt-1" style={{ color: '#007AFF' }} /> en la barra de Safari.</span>
                                    : <span>Toca el menú <b>⋮</b> arriba a la derecha de Chrome.</span>}
                            </li>
                            <li className="flex items-center gap-3">
                                <span className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ background: brand }}>2</span>
                                {ios
                                    ? <span>Elige <b>Agregar a inicio</b> <PlusSquare size={16} className="inline -mt-1" /> y luego <b>Agregar</b>.</span>
                                    : <span>Elige <b>Instalar aplicación</b> o <b>Agregar a la pantalla principal</b>.</span>}
                            </li>
                        </ol>
                        <p className="text-xs text-[#6B7280] mt-4">
                            {ios ? <>Debe ser en <b>Safari</b>. </> : null}Después abre {name} desde el ícono en tu pantalla de inicio.
                        </p>
                        <button onClick={() => setShowGuide(false)} className="w-full h-12 mt-5 rounded-xl text-white font-semibold" style={{ background: brand }}>
                            Entendido
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default InstallAppBanner;
